import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ArrowPathIcon,
  ClipboardDocumentCheckIcon,
  ExclamationTriangleIcon,
  PlayIcon,
  ServerStackIcon,
} from '@heroicons/react/24/outline';
import { api } from '../services/api';
import { useTheme } from '../contexts/ThemeContext';

const WINDOW_OPTIONS = [
  { sec: 30, label: '30s' },
  { sec: 60, label: '60s' },
];

const VERDICT_STYLES = {
  healthy: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
  degraded: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  unhealthy: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  unknown: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
};

const LOG_LEVEL_STYLES = {
  info: 'text-slate-600 dark:text-slate-400',
  warn: 'text-amber-600 dark:text-amber-400',
  error: 'text-red-600 dark:text-red-400',
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
};

const timeAgo = (value) => {
  if (!value) return '—';
  const ms = Date.now() - new Date(value).getTime();
  if (Number.isNaN(ms)) return '—';
  const secs = Math.floor(ms / 1000);
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  return `${Math.floor(mins / 60)}h ago`;
};

const StatCard = ({ label, value, sub, accent, isDarkMode }) => (
  <div className={`rounded-xl border p-4 ${isDarkMode ? 'bg-slate-800/60 border-slate-700' : 'bg-white border-slate-200'}`}>
    <p className={`text-xs font-medium uppercase tracking-wide ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{label}</p>
    <p className={`mt-1 text-2xl font-semibold tabular-nums ${accent || (isDarkMode ? 'text-white' : 'text-slate-900')}`}>{value}</p>
    {sub && <p className={`mt-1 text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>{sub}</p>}
  </div>
);

const LedgerHealthTab = () => {
  const { isDarkMode } = useTheme();
  const [snapshot, setSnapshot] = useState(null);
  const [snapshotLoading, setSnapshotLoading] = useState(true);
  const [pipelineLogs, setPipelineLogs] = useState([]);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState(null);
  const [windowSeconds, setWindowSeconds] = useState(60);
  const [checkState, setCheckState] = useState('idle'); // idle | running | done
  const [activeCheck, setActiveCheck] = useState(null);
  const [remainingSec, setRemainingSec] = useState(0);
  const [checkResult, setCheckResult] = useState(null);
  const pollRef = useRef(null);
  const countdownRef = useRef(null);

  const fetchSnapshot = useCallback(async () => {
    try {
      setSnapshotLoading(true);
      setError(null);
      const [snapRes, logsRes, histRes] = await Promise.all([
        api.get('/ledger-health/snapshot'),
        api.get('/ledger-health/logs', { params: { limit: 80 } }),
        api.get('/ledger-health/checks', { params: { limit: 10 } }),
      ]);
      setSnapshot(snapRes.data?.snapshot || null);
      setPipelineLogs(logsRes.data?.logs || []);
      setHistory(histRes.data?.checks || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load ledger health snapshot');
    } finally {
      setSnapshotLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSnapshot();
    const interval = setInterval(fetchSnapshot, 15_000);
    return () => clearInterval(interval);
  }, [fetchSnapshot]);

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
  }, []);

  const finalizeCheck = async (checkId) => {
    try {
      const res = await api.post(`/ledger-health/checks/${checkId}/finalize`);
      setCheckResult(res.data?.check || null);
      setCheckState('done');
      setRemainingSec(0);
      if (pollRef.current) clearInterval(pollRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
      fetchSnapshot();
    } catch (err) {
      if (err.response?.status === 409) {
        setRemainingSec(err.response?.data?.remainingSec ?? remainingSec);
        return;
      }
      setError(err.response?.data?.error || 'Failed to finalize check');
      setCheckState('idle');
    }
  };

  const startCheck = async () => {
    try {
      setError(null);
      setCheckResult(null);
      setCheckState('running');
      const res = await api.post('/ledger-health/checks', { windowSeconds });
      const check = res.data?.check;
      setActiveCheck(check);
      setRemainingSec(check.windowSeconds);

      if (countdownRef.current) clearInterval(countdownRef.current);
      countdownRef.current = setInterval(() => {
        setRemainingSec((prev) => Math.max(0, prev - 1));
      }, 1000);

      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(() => {
        finalizeCheck(check.id);
      }, 2000);

      setTimeout(() => finalizeCheck(check.id), check.windowSeconds * 1000 + 500);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start check');
      setCheckState('idle');
    }
  };

  const displayLogs = checkResult?.logs?.length ? checkResult.logs : pipelineLogs;
  const verdict = checkResult?.verdict;
  const captureRate = checkResult?.captureRate;

  return (
    <div className="space-y-6">
      <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-slate-800/40 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <ClipboardDocumentCheckIcon className={`w-6 h-6 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
              <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Ledger capture checker</h2>
            </div>
            <p className={`mt-1 text-sm max-w-2xl ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
              Compares VPS follow events (<code className="text-xs">accountResults.followed</code>) against MongoDB ledger writes
              during a live window. Use this to verify capture rate, queue depth, and write lag while automation is running.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={windowSeconds}
              onChange={(e) => setWindowSeconds(Number(e.target.value))}
              disabled={checkState === 'running'}
              className={`text-sm rounded-lg border px-3 py-2 ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-slate-300'}`}
            >
              {WINDOW_OPTIONS.map((o) => (
                <option key={o.sec} value={o.sec}>{o.label} window</option>
              ))}
            </select>
            <button
              type="button"
              onClick={startCheck}
              disabled={checkState === 'running'}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <PlayIcon className="w-4 h-4" />
              {checkState === 'running' ? `Measuring… ${remainingSec}s` : 'Run check'}
            </button>
            <button
              type="button"
              onClick={fetchSnapshot}
              disabled={snapshotLoading}
              className={`inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border ${
                isDarkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-slate-300 text-slate-600 hover:bg-slate-100'
              }`}
            >
              <ArrowPathIcon className={`w-4 h-4 ${snapshotLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="mt-4 flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3 text-sm text-red-700 dark:text-red-300">
            <ExclamationTriangleIcon className="w-5 h-5 shrink-0 mt-0.5" />
            {error}
          </div>
        )}

        {checkResult && (
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium capitalize ${VERDICT_STYLES[verdict] || VERDICT_STYLES.unknown}`}>
              {verdict || 'unknown'}
            </span>
            {captureRate != null && (
              <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                {captureRate}% capture ({checkResult.totals?.ledgerDelta}/{checkResult.totals?.eventsDelta} events)
              </span>
            )}
            <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
              Finished {formatDateTime(checkResult.finishedAt)}
            </span>
          </div>
        )}
      </div>

      {/* Live snapshot */}
      <div>
        <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
          <ServerStackIcon className="w-4 h-4" />
          Live pipeline
          {snapshot?.capturedAt && (
            <span className={`font-normal text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
              · updated {timeAgo(snapshot.capturedAt)}
            </span>
          )}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <StatCard
            label="Running jobs"
            value={snapshot?.runningJobs ?? '—'}
            isDarkMode={isDarkMode}
          />
          <StatCard
            label="Queue depth"
            value={snapshot?.pipeline?.queueSize ?? '—'}
            sub={snapshot?.pipeline?.queueSize > 100 ? 'High — flushes may lag' : undefined}
            accent={snapshot?.pipeline?.queueSize > 100 ? 'text-amber-500' : undefined}
            isDarkMode={isDarkMode}
          />
          <StatCard
            label="Write lag"
            value={snapshot?.pipeline?.lastWriteLagSec != null ? `${snapshot.pipeline.lastWriteLagSec}s` : '—'}
            sub={snapshot?.pipeline?.lastWriteAt ? timeAgo(snapshot.pipeline.lastWriteAt) : undefined}
            accent={
              snapshot?.pipeline?.lastWriteLagSec > 30 ? 'text-amber-500'
                : snapshot?.pipeline?.lastWriteLagSec != null ? 'text-emerald-500' : undefined
            }
            isDarkMode={isDarkMode}
          />
          <StatCard
            label="Ledger (1m)"
            value={snapshot?.totals?.ledgerLast1m ?? '—'}
            isDarkMode={isDarkMode}
          />
          <StatCard
            label="Events total"
            value={snapshot?.totals?.accountResultsFollowed ?? '—'}
            sub="sum accountResults.followed"
            isDarkMode={isDarkMode}
          />
          <StatCard
            label="Flush lock"
            value={
              snapshot?.pipeline?.flushLock?.held == null ? '—'
                : snapshot.pipeline.flushLock.held ? 'Held' : 'Free'
            }
            sub={snapshot?.pipeline?.flushLock?.ttlSec ? `TTL ${snapshot.pipeline.flushLock.ttlSec}s` : undefined}
            isDarkMode={isDarkMode}
          />
        </div>
      </div>

      {/* Check result table */}
      {(checkResult?.perJob?.length > 0 || snapshot?.jobs?.length > 0) && (
        <div className={`rounded-xl border overflow-hidden ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
          <div className={`px-4 py-3 border-b ${isDarkMode ? 'bg-slate-800/80 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
            <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              {checkResult ? 'Check breakdown (per job)' : 'Running jobs'}
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className={isDarkMode ? 'bg-slate-800/50 text-slate-400' : 'bg-slate-50 text-slate-500'}>
                <tr>
                  <th className="px-4 py-2 text-left font-medium">Agent</th>
                  <th className="px-4 py-2 text-left font-medium">Status</th>
                  {checkResult ? (
                    <>
                      <th className="px-4 py-2 text-right font-medium">Events Δ</th>
                      <th className="px-4 py-2 text-right font-medium">Ledger Δ</th>
                      <th className="px-4 py-2 text-right font-medium">Capture</th>
                    </>
                  ) : (
                    <>
                      <th className="px-4 py-2 text-right font-medium">Events</th>
                      <th className="px-4 py-2 text-right font-medium">Progress</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className={isDarkMode ? 'divide-y divide-slate-700' : 'divide-y divide-slate-100'}>
                {(checkResult?.perJob || snapshot?.jobs || []).map((row) => {
                  const capture = checkResult && row.eventsDelta > 0
                    ? Math.round((row.ledgerDelta / row.eventsDelta) * 100)
                    : null;
                  return (
                    <tr key={row.jobId} className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>
                      <td className="px-4 py-2 font-medium">{row.agentName}</td>
                      <td className="px-4 py-2 capitalize">{row.status}</td>
                      {checkResult ? (
                        <>
                          <td className="px-4 py-2 text-right tabular-nums">+{row.eventsDelta}</td>
                          <td className="px-4 py-2 text-right tabular-nums">+{row.ledgerDelta ?? 0}</td>
                          <td className={`px-4 py-2 text-right tabular-nums font-medium ${
                            capture == null ? ''
                              : capture >= 95 ? 'text-emerald-500'
                                : capture >= 80 ? 'text-amber-500' : 'text-red-500'
                          }`}>
                            {capture != null ? `${capture}%` : '—'}
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-4 py-2 text-right tabular-nums">{row.accountResultsFollowed}</td>
                          <td className="px-4 py-2 text-right tabular-nums">{row.progressFollowed}</td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {checkResult?.explanation && (
        <div className={`text-xs rounded-lg p-3 ${isDarkMode ? 'bg-slate-800/50 text-slate-400' : 'bg-slate-100 text-slate-600'}`}>
          <p><strong>Events:</strong> {checkResult.explanation.eventsSource}</p>
          <p className="mt-1"><strong>Ledger:</strong> {checkResult.explanation.ledgerSource}</p>
          <p className="mt-1"><strong>Gap:</strong> {checkResult.explanation.gapMeans}</p>
        </div>
      )}

      {/* Logs */}
      <div className={`rounded-xl border ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
        <div className={`px-4 py-3 border-b flex items-center justify-between ${isDarkMode ? 'bg-slate-800/80 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
          <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            {checkResult ? 'Check logs' : 'Pipeline logs (this pod)'}
          </h3>
          <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
            {displayLogs.length} entries
          </span>
        </div>
        <div className={`max-h-80 overflow-y-auto font-mono text-xs p-3 space-y-1 ${isDarkMode ? 'bg-slate-900/50' : 'bg-white'}`}>
          {displayLogs.length === 0 ? (
            <p className={isDarkMode ? 'text-slate-500' : 'text-slate-400'}>No logs yet — run a check or wait for pipeline events.</p>
          ) : (
            displayLogs.map((log, i) => (
              <div key={`${log.at}-${i}`} className="flex gap-2">
                <span className={`shrink-0 ${isDarkMode ? 'text-slate-600' : 'text-slate-400'}`}>
                  {new Date(log.at).toLocaleTimeString()}
                </span>
                <span className={`shrink-0 uppercase w-12 ${LOG_LEVEL_STYLES[log.level] || LOG_LEVEL_STYLES.info}`}>
                  {log.level}
                </span>
                <span className={`shrink-0 w-24 truncate ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                  {log.code}
                </span>
                <span className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>{log.message}</span>
              </div>
            ))
          )}
        </div>
      </div>

      {/* History */}
      {history.length > 0 && (
        <div className={`rounded-xl border overflow-hidden ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
          <div className={`px-4 py-3 border-b ${isDarkMode ? 'bg-slate-800/80 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
            <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Recent checks</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className={isDarkMode ? 'bg-slate-800/50 text-slate-400' : 'bg-slate-50 text-slate-500'}>
                <tr>
                  <th className="px-4 py-2 text-left font-medium">When</th>
                  <th className="px-4 py-2 text-left font-medium">Window</th>
                  <th className="px-4 py-2 text-left font-medium">Verdict</th>
                  <th className="px-4 py-2 text-right font-medium">Capture</th>
                  <th className="px-4 py-2 text-right font-medium">Events</th>
                  <th className="px-4 py-2 text-right font-medium">Ledger</th>
                </tr>
              </thead>
              <tbody className={isDarkMode ? 'divide-y divide-slate-700' : 'divide-y divide-slate-100'}>
                {history.map((h) => (
                  <tr
                    key={h._id}
                    className={`cursor-pointer ${isDarkMode ? 'text-slate-300 hover:bg-slate-800/50' : 'text-slate-700 hover:bg-slate-50'}`}
                    onClick={async () => {
                      try {
                        const res = await api.get(`/ledger-health/checks/${h._id}`);
                        setCheckResult(res.data?.check || null);
                        setCheckState('done');
                      } catch {
                        /* ignore */
                      }
                    }}
                  >
                    <td className="px-4 py-2">{formatDateTime(h.startedAt)}</td>
                    <td className="px-4 py-2">{h.windowSeconds}s</td>
                    <td className="px-4 py-2">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs capitalize ${VERDICT_STYLES[h.verdict] || VERDICT_STYLES.unknown}`}>
                        {h.verdict}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">{h.captureRate != null ? `${h.captureRate}%` : '—'}</td>
                    <td className="px-4 py-2 text-right tabular-nums">+{h.totals?.eventsDelta ?? '—'}</td>
                    <td className="px-4 py-2 text-right tabular-nums">+{h.totals?.ledgerDelta ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default LedgerHealthTab;
